import * as React from 'react';
import type { strokeDashTypes } from 'react-financial-charts';
import { getMouseCanvas, GenericChartComponent, getStrokeDasharrayCanvas } from 'react-financial-charts';
import type { MoreProps } from '@/types/commontypes';
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const ChartComponent = GenericChartComponent as unknown as React.ComponentType<Record<string, unknown>>;
export interface ClickableArrowHeadProps {
  readonly onDragStart?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDrag?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly strokeWidth: number;
  readonly strokeStyle: string;
  readonly strokeDasharray?: strokeDashTypes;
  readonly fillStyle: string;
  readonly r: number; // You can use r for the size of the arrowhead if needed
  readonly startXY: [number, number];
  readonly endXY: [number, number];
  readonly className: string;
  readonly show: boolean;
  readonly interactiveCursorClass?: string;
  readonly xyProvider?: (moreProps: MoreProps) => number[];
  readonly arrowDirection: 'left' | 'right' | 'both' | 'none';
  usePixelCoordinates?: boolean;
}

export class ClickableArrowHead extends React.Component<ClickableArrowHeadProps> {
  public static defaultProps = {
    className: 'react-financial-charts-interactive-line-edge',
    show: false,
    usePixelCoordinates: false,
    strokeDasharray: 'Solid',
  };
  private hoveredEdge?: 'edge1' | 'edge2';

  public render() {
    const { interactiveCursorClass, onDragComplete, show } = this.props;

    if (!show) {
      return null;
    }

    return (
      <ChartComponent
        interactiveCursorClass={interactiveCursorClass}
        selected
        onDragStart={(e: React.MouseEvent, mp: MoreProps) => {
          if (this.hoveredEdge === 'edge1') this.props.onDragStart?.(e, mp);
          else if (this.hoveredEdge === 'edge2') this.props.onDragStart?.(e, mp);
        }}
        onDrag={(e: React.MouseEvent, mp: MoreProps) => {
          if (this.hoveredEdge === 'edge1') this.props.onDrag?.(e, mp);
          else if (this.hoveredEdge === 'edge2') this.props.onDrag?.(e, mp);
        }}
        onDragComplete={onDragComplete}
        canvasDraw={this.drawOnCanvas}
        canvasToDraw={getMouseCanvas}
        drawOn={['pan', 'mousemove', 'drag']}
      />
    );
  }

  private readonly drawOnCanvas = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    const { strokeStyle, strokeWidth, fillStyle, r, arrowDirection, strokeDasharray } = this.props;
    const lineDash = getStrokeDasharrayCanvas(strokeDasharray);
    ctx.setLineDash(lineDash);
    ctx.lineWidth = strokeWidth;
    ctx.fillStyle = fillStyle;
    ctx.strokeStyle = strokeStyle;

    const [x1, y1, x2, y2] = this.helper(moreProps);
    if (arrowDirection === 'left' || arrowDirection === 'both') {
      drawArrowHeadAt(ctx, x1, y1, x2, y2, r);
    }

    if (arrowDirection === 'right' || arrowDirection === 'both') {
      drawArrowHeadAt(ctx, x2, y2, x1, y1, r);
    }
  };

  private readonly helper = (moreProps: MoreProps) => {
    const { xyProvider, endXY, startXY, usePixelCoordinates } = this.props;

    if (xyProvider !== undefined) {
      return xyProvider(moreProps);
    }

    const {
      xScale,
      chartConfig: { yScale },
    } = moreProps;

    const x1 = usePixelCoordinates ? startXY[0] : xScale(startXY[0]);
    const y1 = usePixelCoordinates ? startXY[1] : yScale(startXY[1]);
    const x2 = usePixelCoordinates ? endXY[0] : xScale(endXY[0]);
    const y2 = usePixelCoordinates ? endXY[1] : yScale(endXY[1]);
    return [x1, y1, x2, y2];
  };
}

export const drawArrowHeadAt = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  targetX: number,
  targetY: number,
  r: number,
) => {
  const dx = x - targetX;
  const dy = y - targetY;
  const angle = Math.atan2(dy, dx);

  const length = r * 2.5;
  const arrowAngle = Math.PI / 6;

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);

  const xLeft = -length * Math.cos(arrowAngle);
  const yLeft = -length * Math.sin(arrowAngle);
  const yRight = length * Math.sin(arrowAngle);

  ctx.beginPath();
  ctx.moveTo(xLeft, yLeft);
  ctx.lineTo(0, 0);
  ctx.lineTo(xLeft, yRight);
  ctx.stroke();

  ctx.restore();
};
